import React, { useState } from 'react';
import { auth, googleProvider } from '../auth';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../db';
import { TermsOfServiceModal, PrivacyPolicyModal } from './LegalModals';
import { acceptInvitationIfExists } from '../utils/managers';
import { IS_CHINA_MODE, getDeviceToken } from '../chinaMode';   // 🇨🇳 V120
import { useLang } from '../i18n/LangContext';   // V144 로그인·회원가입 화면도 한/中
import { APP_VERSION } from '../version';   // V146

const ADMIN_EMAIL = 'u012me@gmail.com';

// ── V109.1: 로그인 실패 원인을 숨기지 않는다 ─────────────────────────────────
// 예전엔 모든 실패가 "이메일 또는 비밀번호를 확인해주세요" 하나로 나와서
// 보안 확인(App Check) 실패·네트워크 차단·시도 횟수 초과까지 비밀번호 문제처럼 보였다.
// (2026-10-10 허페이에서 VPN 경유 로그인이 막혔을 때 원인을 못 찾은 이유)
// 끝에 오류 코드를 붙여 화면 캡처만 받아도 원인을 알 수 있게 한다.
// V144: 화면 언어(한/中)에 맞춰 한 가지 말로 보여준다 (예전엔 한중 병기). 오류 코드는 그대로 붙인다.
function authErrorMessage(e, retried = false, lang = 'ko') {
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const code = String(e?.code || '').replace(/\.+$/, '');   // V120: SDK가 끝에 마침표를 붙여 주는 코드가 있다
  const tag = code ? ` (${code.replace('auth/', '')})` : '';
  switch (code) {
    case 'auth/email-already-in-use': return L('이미 가입된 이메일입니다', '该邮箱已注册');
    case 'auth/weak-password':        return L('비밀번호는 6자 이상이어야 합니다', '密码至少6位');
    case 'auth/invalid-email':        return L('이메일 형식이 올바르지 않습니다', '邮箱格式不正确');
    case 'auth/missing-password':     return L('비밀번호를 입력해주세요', '请输入密码');
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return L('이메일 또는 비밀번호가 맞지 않습니다', '邮箱或密码不正确') + tag;
    case 'auth/too-many-requests':
      return L('로그인 시도가 너무 많아 잠시 막혔습니다. 15분 뒤 다시 시도하거나 비밀번호를 재설정해주세요',
               '尝试次数过多，暂时被限制。请15分钟后再试，或重置密码') + tag;
    case 'auth/network-request-failed':
      return L((retried ? '3번 시도했지만 ' : '') + '서버에 연결하지 못했습니다. 인터넷·VPN 연결을 확인하고 잠시 뒤 다시 눌러주세요',
               (retried ? '已尝试3次，' : '') + (IS_CHINA_MODE ? '无法连接服务器，请检查网络后重试' : '无法连接服务器，请检查网络和VPN后重试')) + tag;
    case 'auth/firebase-app-check-token-is-invalid':
      if (IS_CHINA_MODE) return L('이 기기가 아직 등록되지 않았어요. 아래 "기기 등록 코드"를 사장님께 보내주세요', '本设备尚未登记，请把下面的「本设备注册码」发给老板') + tag;
      return L('보안 확인(App Check)에 실패했습니다. VPN 접속에서 생길 수 있어요 — 이 화면을 관리자에게 보내주세요', '安全验证失败，可能是VPN造成的，请把此画面发给管理员') + tag;
    case 'auth/user-disabled':
      return L('사용이 중지된 계정입니다', '该账号已停用') + tag;
    default:
      return L('로그인에 실패했습니다', '登录失败') + (tag || ` (${String(e?.message || 'unknown').slice(0, 80)})`);
  }
}

// ── V117: 네트워크 오류는 자동으로 다시 시도 ─────────────────────────────────
// 허페이는 VPN(싱가포르) 경유라 연결이 순간적으로 끊기는 일이 잦다. 예전엔 요청 한 번이
// 끊기면 바로 "서버에 연결하지 못했습니다"를 띄워서, 같은 비밀번호로 "어떨 땐 되고 어떨 땐 안 되는"
// 것처럼 보였다. 로그인은 몇 번 보내도 결과가 같으므로(멱등) 재시도해도 안전하다.
// 회원가입은 서버에선 계정이 이미 만들어졌는데 응답만 끊겼을 수 있어 재시도하지 않는다.
const NET_RETRY_WAITS = [1500, 3000];   // 최대 3번 시도
async function withNetRetry(fn, onRetry) {
  for (let i = 0; ; i++) {
    try { return await fn(); }
    catch (e) {
      if (e?.code !== 'auth/network-request-failed' || i >= NET_RETRY_WAITS.length) throw e;
      onRetry?.(i + 2, NET_RETRY_WAITS.length + 1);
      await new Promise(r => setTimeout(r, NET_RETRY_WAITS[i]));
    }
  }
}

// V144: 중국어 화면에서 보이는 이름 (저장 값은 한국어 그대로 — 관리자 승인 목록이 한국어)
const OPTION_ZH = {
  '지인 소개': '朋友介绍', '인스타그램 / SNS': '社交媒体（小红书·抖音等）', '네이버 / 구글 검색': '网络搜索',
  '유튜브': '视频平台', '박람회 / 행사': '展会 / 活动', '기타': '其他',
  '외식업 · 식음료': '餐饮',
  '한식당': '韩餐厅', '중식당': '中餐厅', '일식당': '日料店', '양식당': '西餐厅',
  '분식 · 김밥': '韩式小吃 · 紫菜包饭', '치킨 · 패스트푸드': '炸鸡 · 快餐',
  '카페 · 베이커리': '咖啡 · 烘焙', '해산물 · 횟집': '海鲜', '고기 · 구이': '烤肉',
  '주점 · 바': '酒吧', '푸드트럭 · 배달전문': '餐车 · 外卖', '기타 외식업': '其他餐饮',
};

const REFERRAL_OPTIONS = [
  '지인 소개',
  '인스타그램 / SNS',
  '네이버 / 구글 검색',
  '유튜브',
  '박람회 / 행사',
  '기타',
];

// V28: 셈핏은 외식업·식음료 단일 앱으로 집중.
// 다른 업종은 PMF 후 결정 (앱 분리 또는 가입 시 분기).
const INDUSTRY_GROUPS = [
  {
    group: '외식업 · 식음료',
    items: [
      '한식당', '중식당', '일식당', '양식당',
      '분식 · 김밥', '치킨 · 패스트푸드',
      '카페 · 베이커리', '해산물 · 횟집',
      '고기 · 구이', '주점 · 바',
      '푸드트럭 · 배달전문', '기타 외식업',
    ],
  },
];

// ── V106 접근 게이트 ─────────────────────────────────────────────────────
// 2단 구조:
//   1단 — 매장 사장(owner)은 관리자(u012me) 승인 목록에 있어야 한다.
//   2단 — 그 사장이 설정에서 초대한 매니저·직원은 관리자 승인 없이 들어온다.
//          (사장이 이미 신뢰한 사람이므로 관리자가 또 승인할 이유가 없다)
//
// 이전 구현에는 구멍이 두 개 있었다:
//   · 이메일/비번 가입자는 무조건 통과 → 승인 목록이 아무 의미가 없었음
//   · 구글 로그인도 users/{uid} 문서만 있으면 통과 → 이미 가입한 사람은 못 막음
// 둘 다 제거했다.
//
// 네트워크 오류 시에는 통과시킨다(fail-open). 허페이 매장은 VPN 경유라
// Firestore 읽기가 종종 실패하는데, 그때 직원이 매장에서 잠기면 안 된다.
async function checkAllowlist(user) {
  if (user.email === ADMIN_EMAIL) return true;
  const email = String(user.email || '').trim().toLowerCase();
  try {
    // 1) 관리자 승인 목록
    const snap = await getDoc(doc(db, 'config', 'allowlist'));
    if (!snap.exists()) return true;              // 목록 자체가 없으면 오픈 취급
    const { emails = [], open = false } = snap.data();
    if (open) return true;
    if (email && emails.some(e => String(e || '').trim().toLowerCase() === email)) return true;

    // 2) 사장이 보낸 초대장이 있는 사람 (가입 직후 매니저·직원)
    if (email) {
      try {
        const inv = await getDoc(doc(db, 'invitations', email));
        if (inv.exists()) return true;
      } catch (e) { /* 초대장 조회 실패는 무시하고 다음 단계로 */ }
    }

    // 3) 이미 어느 매장의 매니저로 등록된 사람
    //    (사장이 승인하면 초대장은 삭제되므로 그 이후엔 이 조건으로 통과한다)
    try {
      const me = await getDoc(doc(db, 'users', user.uid));
      const owners = me.exists() ? me.data()?.managedOwnerUids : null;
      if (Array.isArray(owners) && owners.length > 0) return true;
    } catch (e) { /* 무시 */ }

    return false;
  } catch (e) {
    console.error('allowlist 확인 실패:', e);
    return true;   // fail-open — 네트워크 문제로 매장을 잠그지 않는다
  }
}

// 접근 신청 저장 (추가 프로필 포함)
async function saveAccessRequest(user, extra = {}) {
  try {
    await setDoc(doc(db, 'accessRequests', user.uid), {
      email:           user.email,
      name:            extra.managerName || user.displayName || '',
      photo:           user.photoURL || '',
      storeName:       extra.storeName      || '',
      industry:        extra.industry       || '',
      referral:        extra.referral       || '',
      agreeTerms:      extra.agreeTerms     ?? null,
      agreePrivacy:    extra.agreePrivacy   ?? null,
      agreeMarketing:  extra.agreeMarketing ?? null,
      agreedAt:        extra.agreedAt       || null,
      requestedAt:     new Date().toISOString(),
      status:          'pending',
    }, { merge: true });
  } catch (e) { /* 무시 */ }
}

// 사용자 기본 프로필 저장
async function saveUserProfile(user, extra = {}) {
  try {
    await setDoc(doc(db, 'users', user.uid), {
      email:           user.email,
      name:            extra.managerName || user.displayName || '',
      storeName:       extra.storeName      || '',
      industry:        extra.industry       || '',
      referral:        extra.referral       || '',
      agreeTerms:      extra.agreeTerms     ?? null,
      agreePrivacy:    extra.agreePrivacy   ?? null,
      agreeMarketing:  extra.agreeMarketing ?? null,
      agreedAt:        extra.agreedAt       || null,
      createdAt:       new Date().toISOString(),
      ...(extra.lang === 'zh' || extra.lang === 'ko' ? { lang: extra.lang } : {}),   // V144: 가입할 때 고른 언어를 계정에도 (다른 기기에서도 같은 언어)
    }, { merge: true });
  } catch (e) { /* 무시 */ }
}

const inp = {
  width: '100%', padding: '13px 14px',
  border: '1px solid #F4F4F5', borderRadius: '10px',
  marginBottom: '10px', fontSize: '14px', boxSizing: 'border-box',
  outline: 'none', background: '#fff', color: '#18181B',
  transition: 'border-color 0.15s',
};

// ── In-app browser 감지 ────────────────────────────────────────────────────
// 카카오톡·라인·페이스북·인스타·네이버앱 등의 웹뷰. Google OAuth가 차단됨.
function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/SemfitApp\//.test(ua)) return null;   // V140: 셈핏 안드로이드 앱(중국 직원용)은 안내 안 함
  if (IS_CHINA_MODE) return null;            // V144: 이 안내는 구글 로그인용 — 중국 모드엔 구글 로그인이 없다
  if (/KAKAOTALK/i.test(ua)) return 'kakaotalk';
  if (/Line\//i.test(ua)) return 'line';
  if (/FBAN|FBAV|Instagram/i.test(ua)) return 'facebook';
  if (/NAVER\(/i.test(ua)) return 'naver';
  if (/wv\)/i.test(ua) || /; wv;/i.test(ua)) return 'webview';  // 일반 안드로이드 웹뷰
  return null;
}

// In-app 안내 배너
function InAppBrowserBanner({ inAppType }) {
  const { lang } = useLang();   // V144
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [copied, setCopied] = React.useState(false);
  const url = window.location.href;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert(L('주소: ', '网址：') + url);
    }
  };

  // 카카오톡: closeBrowser 시도 (외부 브라우저 자동 호출)
  const openExternal = () => {
    if (inAppType === 'kakaotalk') {
      // KakaoTalk inApp browser → 외부 브라우저 호출
      window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`;
    } else if (inAppType === 'line') {
      window.location.href = url + (url.includes('?') ? '&' : '?') + 'openExternalBrowser=1';
    } else {
      // fallback: 그냥 새 창 시도 (보통은 같은 in-app에서 열림)
      window.open(url, '_blank');
    }
  };

  const appLabel = (lang === 'zh' ? {
    kakaotalk: 'KakaoTalk', line: 'LINE', facebook: 'Facebook·Instagram', naver: 'Naver', webview: 'App 内置浏览器',
  } : {
    kakaotalk: '카카오톡',
    line: '라인',
    facebook: '페이스북·인스타',
    naver: '네이버앱',
    webview: '앱 안 브라우저',
  })[inAppType] || L('앱 안 브라우저', 'App 内置浏览器');

  return (
    <div style={{
      background: '#F4F4F5', border: '2px solid #A1A1AA', borderRadius: 14,
      padding: '18px 20px', margin: '0 0 20px 0',
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>⚠️</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#52525B', marginBottom: 6 }}>
        {L(`${appLabel} 안에서 보고 계시네요`, `正在 ${appLabel} 里浏览`)}
      </div>
      <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6, marginBottom: 14 }}>
        {lang === 'zh'
          ? <>谷歌登录只能在<strong>外部浏览器（Chrome/Safari）</strong>中使用。<br />可以点下面的按钮用外部浏览器打开，或者<strong>用邮箱注册</strong>，马上就能使用。</>
          : <>Google 로그인은 <strong>외부 브라우저(Chrome/Safari)</strong>에서만 가능해요.<br />
        아래 버튼으로 외부 브라우저로 열거나, <strong>이메일로 가입</strong>하시면 바로 사용 가능합니다.</>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={openExternal} type="button"
          style={{
            flex: 1, minWidth: 140, padding: '11px 14px', borderRadius: 10, border: 'none',
            background: '#FF6F0F', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer',
          }}>
          {L('🌐 외부 브라우저로 열기', '🌐 用外部浏览器打开')}
        </button>
        <button onClick={copyUrl} type="button"
          style={{
            flex: 1, minWidth: 140, padding: '11px 14px', borderRadius: 10,
            border: '1.5px solid #52525B', background: '#fff',
            color: '#52525B', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>
          {copied ? L('✓ 복사됨', '✓ 已复制') : L('📋 주소 복사', '📋 复制网址')}
        </button>
      </div>

      <div style={{ fontSize: 11, color: '#52525B', marginTop: 10 }}>
        {L('💡 외부 버튼이 안 되면: 우측 하단 ⋮ 메뉴 → "다른 브라우저로 열기"', '💡 如果按钮没反应：点菜单 ⋮ →「在浏览器打开」')}
      </div>
    </div>
  );
}

function Login() {
  // V144: 로그인·회원가입 화면도 한/中 — 중국 직원에게 보내면 한국어로만 나왔다
  const { lang, setLang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const optLabel = (o) => (lang === 'zh' && OPTION_ZH[o] ? OPTION_ZH[o] : o);
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [isRegister,  setIsRegister]  = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [retryMsg,    setRetryMsg]    = useState('');   // V117: "연결 재시도 중 2/3"
  const [appCheckFailed, setAppCheckFailed] = useState(false);   // V120: 중국 모드 미등록 기기
  const [info,        setInfo]        = useState('');   // V109.1: 비밀번호 재설정 안내

  // 회원가입 추가 정보
  const [storeName,   setStoreName]   = useState('');
  const [managerName, setManagerName] = useState('');
  const [referral,    setReferral]    = useState('');
  const [industry,    setIndustry]    = useState('');

  // 약관 동의
  const [agreeTerms,   setAgreeTerms]   = useState(false); // 이용약관 (필수)
  const [agreePrivacy, setAgreePrivacy] = useState(false); // 개인정보처리방침 (필수)
  const [agreeMarketing, setAgreeMarketing] = useState(false); // 마케팅 수신 (선택)
  const [agreeAll,     setAgreeAll]     = useState(false);
  // 모달 열기
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleAgreeAll = (v) => {
    setAgreeAll(v);
    setAgreeTerms(v);
    setAgreePrivacy(v);
    setAgreeMarketing(v);
  };

  const syncAgreeAll = (terms, privacy, marketing) => {
    setAgreeAll(terms && privacy && marketing);
  };

  // sessionStorage로 signOut 후에도 메시지 유지
  const [error, setError] = useState(() => {
    const msg = sessionStorage.getItem('ddabak_auth_msg');
    if (msg) { sessionStorage.removeItem('ddabak_auth_msg'); return msg; }
    return '';
  });
  const [blocked, setBlocked] = useState(() => {
    return sessionStorage.getItem('ddabak_blocked') === '1'
      ? (sessionStorage.removeItem('ddabak_blocked'), true)
      : false;
  });

  const switchMode = (toRegister) => {
    setIsRegister(toRegister);
    setError(''); setInfo('');
    setStoreName(''); setManagerName(''); setReferral(''); setIndustry('');
    setAgreeTerms(false); setAgreePrivacy(false); setAgreeMarketing(false); setAgreeAll(false);
  };

  const extra = {
    storeName, managerName, referral, industry,
    agreeTerms, agreePrivacy, agreeMarketing,
    agreedAt: new Date().toISOString(),
  };

  const handleEmail = async () => {
    setError(''); setBlocked(false); setInfo('');
    if (isRegister) {
      if (!storeName.trim())   { setError(L('가게 이름을 입력해주세요', '请输入店名')); return; }
      if (!managerName.trim()) { setError(L('담당자 이름을 입력해주세요', '请输入负责人姓名')); return; }
      if (!industry)           { setError(L('업종을 선택해주세요', '请选择行业')); return; }
      if (!referral)           { setError(L('가입 경로를 선택해주세요', '请选择是怎么知道我们的')); return; }
      if (!agreeTerms)         { setError(L('이용약관에 동의해주세요', '请同意服务条款')); return; }
      if (!agreePrivacy)       { setError(L('개인정보 처리방침에 동의해주세요', '请同意隐私政策')); return; }
    }
    try {
      setLoading(true);
      let result;
      if (isRegister) {
        result = await createUserWithEmailAndPassword(auth, email, password);
        await saveUserProfile(result.user, { ...extra, lang });
        await saveAccessRequest(result.user, extra); // 승인 신청으로 접수
        // V49: 매니저 초대장 자동 매칭
        // V97: 신규 가입 직후 token에 email claim 보장
        try {
          await result.user.getIdToken(true);
          await acceptInvitationIfExists(result.user.uid, result.user.email);
        } catch (err) {
          console.warn('invitation match failed:', err?.code, err?.message);
        }
        // V106: 신규 가입도 승인 게이트를 거친다.
        // 사장이 초대한 직원은 위 매칭으로 초대장/매니저 조건에 걸려 통과한다.
        const newAllowed = await checkAllowlist(result.user);
        if (!newAllowed) {
          sessionStorage.setItem('ddabak_auth_msg', result.user.email);
          sessionStorage.setItem('ddabak_blocked', '1');
          await signOut(auth);
        }
      } else {
        result = await withNetRetry(
          () => signInWithEmailAndPassword(auth, email, password),
          (n, total) => setRetryMsg(L(`연결 재시도 중 ${n}/${total}`, `重新连接中 ${n}/${total}`)),
        );
        setRetryMsg('');
        const allowed = await checkAllowlist(result.user);
        if (!allowed) {
          sessionStorage.setItem('ddabak_auth_msg', result.user.email);
          sessionStorage.setItem('ddabak_blocked', '1');
          await signOut(auth);
        } else {
          // V49: 매니저 초대장 자동 매칭 (이미 가입한 사용자도 재로그인 시 체크)
          // V97: token refresh로 email claim 보장
          try {
            await result.user.getIdToken(true);
            await acceptInvitationIfExists(result.user.uid, result.user.email);
          } catch (err) {
            console.warn('invitation match failed:', err?.code, err?.message);
          }
        }
      }
    } catch (e) {
      console.warn('[login] failed:', e?.code, e?.message);
      setAppCheckFailed(String(e?.code || '').startsWith('auth/firebase-app-check-token-is-invalid'));
      setError(authErrorMessage(e, !isRegister, lang));
    } finally {
      setLoading(false);
      setRetryMsg('');
    }
  };

  // V109.1: 비밀번호 재설정 — 예전엔 로그인 화면에 방법이 아예 없었다
  const handleReset = async () => {
    setError(''); setInfo(''); setBlocked(false);
    const em = String(email || '').trim();
    if (!em) { setError(L('재설정 메일을 받을 이메일을 위 칸에 입력해주세요', '请先在上方输入要接收重置邮件的邮箱')); return; }
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, em);
      // 가입 여부를 노출하지 않는 Firebase 정책상, 없는 이메일이어도 성공으로 돌아온다
      setInfo(L(`${em} 이 가입된 이메일이면 비밀번호 재설정 메일이 발송됩니다. 메일함(스팸함 포함)을 확인해주세요.`,
                `如果 ${em} 是已注册的邮箱，重置密码邮件会发到这个邮箱。请查收（也看看垃圾邮件）。`)
        + (IS_CHINA_MODE ? L(' (메일 속 링크는 중국에서 VPN이 필요할 수 있어요)', '（邮件里的链接在中国可能需要VPN才能打开）') : ''));
    } catch (e) {
      console.warn('[reset] failed:', e?.code, e?.message);
      setError(authErrorMessage(e, false, lang));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(''); setBlocked(false); setInfo('');
    try {
      setLoading(true);
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, googleProvider);
      const allowed = await checkAllowlist(result.user);
      if (!allowed) {
        await saveAccessRequest(result.user, {});
        sessionStorage.setItem('ddabak_auth_msg', result.user.email);
        sessionStorage.setItem('ddabak_blocked', '1');
        await signOut(auth);
      } else {
        // V49: 매니저 초대장 자동 매칭
        // V97: Google 로그인 후 token refresh
        try {
          await result.user.getIdToken(true);
          await acceptInvitationIfExists(result.user.uid, result.user.email);
        } catch (err) {
          console.warn('invitation match failed:', err?.code, err?.message);
        }
      }
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') {
        setError(L(`구글 로그인 실패: ${e.code || e.message}`, `谷歌登录失败：${e.code || e.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── 차단 안내 화면 ──
  if (blocked) {
    const blockedEmail = error || '';
    return (
      <div style={{ minHeight:'100vh', background:'#f0f2f5', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'#fff', borderRadius:'16px', padding:'40px', width:'380px',
                      boxShadow:'0 4px 24px rgba(0,0,0,0.1)', textAlign:'center' }}>
          <div style={{ fontSize:'3rem', marginBottom:'16px' }}>🔒</div>
          <h2 style={{ fontSize:'1.3rem', fontWeight:'800', color:'#111827', marginBottom:'12px' }}>
            {L('승인 대기 중', '等待审核')}
          </h2>
          <p style={{ color:'#6b7280', fontSize:'0.9rem', lineHeight:1.7, marginBottom:'8px' }}>
            <strong style={{ color:'#374151' }}>{blockedEmail}</strong>
          </p>
          <p style={{ color:'#6b7280', fontSize:'0.9rem', lineHeight:1.7, marginBottom:'24px' }}>
            {lang === 'zh'
              ? <>还没有使用权限。<br/>已自动提交使用申请，<br/>老板批准后就可以登录。</>
              : <>아직 접근 권한이 없습니다.<br/>
            사용 승인 신청이 자동으로 접수되었으며<br/>
            사장님 승인 후 로그인하실 수 있습니다.</>}
          </p>
          <div style={{ background:'#FAFAFA', border:'1px solid #F4F4F5', borderRadius:'10px',
                        padding:'12px', marginBottom:'24px' }}>
            <p style={{ fontSize:'0.8rem', color:'#18181B', fontWeight:'700' }}>
              {L('✅ 승인 신청 접수 완료', '✅ 申请已提交')}
            </p>
            <p style={{ fontSize:'0.75rem', color:'#FB923C', marginTop:'4px' }}>
              {L('관리자에게 알림이 전송되었습니다', '已通知管理员')}
            </p>
          </div>
          <button
            onClick={() => { setBlocked(false); setError(''); }}
            style={{ width:'100%', padding:'13px', background:'#FF6F0F', color:'#fff',
                     border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:'700', cursor:'pointer',
                     boxShadow: '0 4px 14px rgba(255,111,15,0.25)' }}>
            {L('← 로그인 화면으로', '← 返回登录')}
          </button>
        </div>
      </div>
    );
  }

  // ── 로그인 / 회원가입 폼 (V58: 토스 스타일 풀 리디자인) ──
  return (
    <div style={{ minHeight:'100vh', background:'#FAFAFA', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
      <div style={{ background:'#fff', borderRadius:'20px', padding:'40px 36px',
                    width:'420px', maxWidth:'100%', border:'1px solid #F4F4F5',
                    boxShadow:'0 10px 30px rgba(17,24,39,0.06), 0 4px 8px rgba(17,24,39,0.04)' }}>

        {/* V144: 언어 선택 — 한국어 / 中文 (기기에 기억, 로그인 뒤에도 그대로) */}
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'-16px', marginBottom:'8px' }}>
          <div data-login-lang style={{ display:'flex', border:'1px solid #E4E4E7', borderRadius:'999px', overflow:'hidden' }}>
            {[['ko', '한국어'], ['zh', '中文']].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setLang(id)} aria-pressed={lang === id}
                style={{ padding:'6px 14px', border:'none', cursor:'pointer', fontSize:'13px',
                         fontWeight: lang === id ? 800 : 600,
                         background: lang === id ? '#FF6F0F' : '#fff', color: lang === id ? '#fff' : '#71717A' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 로고 (V58: 오렌지 심볼 + 굵은 셈핏) */}
        <div style={{ textAlign:'center', marginBottom:'28px' }}>
          <img src="/semfit_symbol_t.png" alt="셈핏"
               style={{ width: 56, height: 56, display: 'inline-block', objectFit: 'contain' }} />
          <h1 style={{ fontSize:'28px', fontWeight:'800', color:'#18181B', marginTop:'12px', letterSpacing:'-0.02em' }}>{L('셈핏', 'SEMFIT')}</h1>
          <p style={{ color:'#71717A', fontSize:'14px', marginTop:'6px', fontWeight:500 }}>{L('외식업 사장님의 운영 OS', '餐饮店的经营管理系统')}</p>
        </div>

        {/* In-app browser 안내 (카카오톡·라인·페북 등) */}
        {(() => {
          const inAppType = detectInAppBrowser();
          return inAppType ? <InAppBrowserBanner inAppType={inAppType} /> : null;
        })()}

        {/* 탭 (V58: 오렌지 활성) */}
        <div style={{ display:'flex', background:'#F4F4F5', borderRadius:'12px', padding:'4px', marginBottom:'24px' }}>
          {[[L('로그인', '登录'), false], [L('회원가입', '注册'), true]].map(([label, reg]) => (
            <button key={label} onClick={() => switchMode(reg)}
              style={{
                flex:1, padding:'10px', border:'none', cursor:'pointer',
                borderRadius:'9px', fontSize:'14px', fontWeight:'700',
                background: isRegister === reg ? '#fff' : 'transparent',
                color:      isRegister === reg ? '#FF6F0F' : '#71717A',
                boxShadow:  isRegister === reg ? '0 1px 3px rgba(17,24,39,0.06), 0 1px 2px rgba(17,24,39,0.04)' : 'none',
                transition: 'all 0.15s',
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* 오류 메시지 (V58) */}
        {error && (
          <div style={{ background:'#F4F4F5', color:'#991B1B', padding:'12px 14px',
                        borderRadius:'10px', marginBottom:'14px', fontSize:'13px', fontWeight: 500,
                        border:'1px solid #E4E4E7' }}>
            {error}
          </div>
        )}
        {info && !error && (
          <div style={{ background:'#F0FDF4', color:'#166534', padding:'12px 14px',
                        borderRadius:'10px', marginBottom:'14px', fontSize:'13px', fontWeight: 500,
                        border:'1px solid #BBF7D0', lineHeight: 1.5 }}>
            {info}
          </div>
        )}

        {/* 회원가입 추가 필드 */}
        {isRegister && (
          <>
            <label style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', display:'block', marginBottom:'5px' }}>
              {L('가게 이름 *', '店名 *')}
            </label>
            <input
              placeholder={L('예: 셈핏 분식', '例：JJGG 韩餐')}
              value={storeName}
              onChange={e => setStoreName(e.target.value)}
              style={inp}
            />

            <label style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', display:'block', marginBottom:'5px' }}>
              {L('담당자 이름 *', '姓名 *')}
            </label>
            <input
              placeholder={L('예: 홍길동', '例：张三')}
              value={managerName}
              onChange={e => setManagerName(e.target.value)}
              style={inp}
            />

            <label style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', display:'block', marginBottom:'5px' }}>
              {L('업종 *', '行业 *')}
            </label>
            <select
              value={industry}
              onChange={e => setIndustry(e.target.value)}
              style={{ ...inp, background:'#fff', color: industry ? '#111' : '#9ca3af' }}>
              <option value="" disabled>{L('업종을 선택해주세요', '请选择行业')}</option>
              {INDUSTRY_GROUPS.map(g => (
                <optgroup key={g.group} label={optLabel(g.group)}>
                  {g.items.map(o => (
                    <option key={o} value={o}>{optLabel(o)}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <label style={{ fontSize:'12px', fontWeight:'700', color:'#6b7280', display:'block', marginBottom:'5px' }}>
              {L('가입 경로 *', '怎么知道我们的 *')}
            </label>
            <select
              value={referral}
              onChange={e => setReferral(e.target.value)}
              style={{ ...inp, background:'#fff', color: referral ? '#111' : '#9ca3af' }}>
              <option value="" disabled>{L('어떻게 알게 되셨나요?', '请选择')}</option>
              {REFERRAL_OPTIONS.map(o => (
                <option key={o} value={o}>{optLabel(o)}</option>
              ))}
            </select>

            <div style={{ borderTop:'1px solid #f3f4f6', margin:'4px 0 14px' }} />
          </>
        )}

        {/* 이메일 / 비밀번호 */}
        <input
          type="email" placeholder={L('이메일', '邮箱')} value={email}
          onChange={e => setEmail(e.target.value)}
          style={inp}
        />
        <input
          type="password"
          placeholder={isRegister ? L('비밀번호 (6자 이상)', '密码（至少6位）') : L('비밀번호', '密码')}
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleEmail()}
          style={{ ...inp, marginBottom:'18px' }}
        />

        {/* 약관 동의 (회원가입 시) */}
        {isRegister && (
          <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'10px',
                        padding:'14px 16px', marginBottom:'16px' }}>

            {/* 전체 동의 */}
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer',
                             paddingBottom:'10px', borderBottom:'1px solid #e5e7eb', marginBottom:'10px' }}>
              <input
                type="checkbox"
                checked={agreeAll}
                onChange={e => handleAgreeAll(e.target.checked)}
                style={{ width:'17px', height:'17px', accentColor:'#FF6F0F', cursor:'pointer', flexShrink:0 }}
              />
              <span style={{ fontSize:'13px', fontWeight:'800', color:'#111827' }}>{L('전체 동의', '全部同意')}</span>
            </label>

            {/* 이용약관 (필수) */}
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer', marginBottom:'8px' }}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={e => {
                  setAgreeTerms(e.target.checked);
                  syncAgreeAll(e.target.checked, agreePrivacy, agreeMarketing);
                }}
                style={{ width:'15px', height:'15px', accentColor:'#FF6F0F', cursor:'pointer', flexShrink:0 }}
              />
              <span style={{ fontSize:'12px', color:'#374151', flex:1 }}>
                <span style={{ color:'#52525B', fontWeight:'700', marginRight:'4px' }}>{L('[필수]', '[必选]')}</span>
                {L('이용약관 동의', '同意服务条款')}
              </span>
              <button type="button" onClick={() => setTermsOpen(true)}
                style={{ background:'none', border:'none', color:'#18181B', fontSize:'11px', fontWeight:'700', cursor:'pointer', textDecoration:'underline', flexShrink:0 }}>
                {L('보기', '查看')}
              </button>
            </label>

            {/* 개인정보 처리방침 (필수) */}
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer', marginBottom:'8px' }}>
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={e => {
                  setAgreePrivacy(e.target.checked);
                  syncAgreeAll(agreeTerms, e.target.checked, agreeMarketing);
                }}
                style={{ width:'15px', height:'15px', accentColor:'#FF6F0F', cursor:'pointer', flexShrink:0 }}
              />
              <span style={{ fontSize:'12px', color:'#374151', flex:1 }}>
                <span style={{ color:'#52525B', fontWeight:'700', marginRight:'4px' }}>{L('[필수]', '[必选]')}</span>
                {L('개인정보 처리방침 동의', '同意隐私政策')}
              </span>
              <button type="button" onClick={() => setPrivacyOpen(true)}
                style={{ background:'none', border:'none', color:'#18181B', fontSize:'11px', fontWeight:'700', cursor:'pointer', textDecoration:'underline', flexShrink:0 }}>
                {L('보기', '查看')}
              </button>
            </label>

            {/* 마케팅 정보 수신 (선택) */}
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
              <input
                type="checkbox"
                checked={agreeMarketing}
                onChange={e => {
                  setAgreeMarketing(e.target.checked);
                  syncAgreeAll(agreeTerms, agreePrivacy, e.target.checked);
                }}
                style={{ width:'15px', height:'15px', accentColor:'#FF6F0F', cursor:'pointer', flexShrink:0 }}
              />
              <span style={{ fontSize:'12px', color:'#374151', flex:1 }}>
                <span style={{ color:'#6b7280', fontWeight:'700', marginRight:'4px' }}>{L('[선택]', '[可选]')}</span>
                {L('마케팅 정보 수신 동의', '同意接收营销信息')}
              </span>
            </label>
            <p style={{ fontSize:'11px', color:'#9ca3af', marginTop:'6px', marginLeft:'25px' }}>
              {L('서비스 업데이트, 새 기능 소식을 이메일로 받아볼 수 있어요.', '可以通过邮件收到服务更新和新功能消息。')}
            </p>
          </div>
        )}

        {/* 제출 버튼 (V58: 오렌지 primary) */}
        <button onClick={handleEmail} disabled={loading}
          style={{ width:'100%', padding:'14px', background:'#FF6F0F', color:'#fff',
                   border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:'700',
                   cursor: loading ? 'not-allowed' : 'pointer',
                   opacity: loading ? 0.7 : 1, marginBottom:'10px',
                   boxShadow: loading ? 'none' : '0 4px 14px rgba(255,111,15,0.25)',
                   transition: 'all 0.15s' }}>
          {loading ? (retryMsg || L('처리 중...', '处理中...')) : isRegister ? L('회원가입', '注册') : L('로그인', '登录')}
        </button>

        {/* V109.1: 비밀번호 재설정 */}
        {!isRegister && (
          <div style={{ textAlign:'right', margin:'-2px 0 10px' }}>
            <button type="button" onClick={handleReset} disabled={loading}
              style={{ background:'none', border:'none', padding:'4px 2px', fontSize:'12px',
                       color:'#71717A', textDecoration:'underline', cursor:'pointer' }}>
              {L('비밀번호를 잊으셨나요?', '忘记密码？')}
            </button>
          </div>
        )}

        {/* 구글 로그인 (V58: 흰 카드 + 보더) — V120: 중국 모드에선 구글 로그인이 안 되므로 숨김 */}
        {!isRegister && !IS_CHINA_MODE && (
          <button onClick={handleGoogle} disabled={loading}
            style={{ width:'100%', padding:'14px', background:'#fff', color:'#18181B',
                     border:'1px solid #F4F4F5', borderRadius:'10px', fontSize:'15px',
                     fontWeight:'600', cursor:'pointer', opacity: loading ? 0.6 : 1,
                     transition: 'all 0.15s' }}>
            {L('🔴 구글로 로그인', '🔴 用谷歌登录')}
          </button>
        )}

        {/* 🇨🇳 V120 중국 모드 — 기기 등록 코드 */}
        {IS_CHINA_MODE && <ChinaDeviceBox highlight={appCheckFailed} lang={lang} />}

        {/* 회원가입 안내 */}
        {isRegister && (
          <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', lineHeight:1.6, marginTop:'12px' }}>
            {L('가입 즉시 무료로 이용하실 수 있습니다.', '注册后即可免费使用。')}
          </p>
     
        )}
        <div data-login-version style={{ marginTop:'14px', textAlign:'center', fontSize:'11px', color:'#A1A1AA' }}>SEMFIT {APP_VERSION}</div>
      </div>
      <LoginModals termsOpen={termsOpen} setTermsOpen={setTermsOpen} privacyOpen={privacyOpen} setPrivacyOpen={setPrivacyOpen} lang={lang} />
    </div>
  );
}

// ── V120: 중국 모드 기기 등록 코드 ───────────────────────────────────────────
// 중국에서는 reCAPTCHA가 막혀 있어서 기기마다 등록 코드(App Check 디버그 토큰)를 쓴다.
// 사장님이 Firebase 콘솔 → App Check → 앱 → ⋮ → 디버그 토큰 관리 → 추가 에 이 코드를 넣으면 그 기기가 열린다.
function ChinaDeviceBox({ highlight, lang = 'ko' }) {
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);   // V144
  const [copied, setCopied] = useState(false);
  const token = getDeviceToken() || '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(token); }
    catch (e) {
      const ta = document.createElement('textarea'); ta.value = token; document.body.appendChild(ta);
      ta.select(); try { document.execCommand('copy'); } catch (err) { /* 무시 */ } ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, lineHeight: 1.6, fontSize: 12, color: '#374151',
                  background: highlight ? '#FFF7ED' : '#F4F7FB', border: `1.5px solid ${highlight ? '#FB923C' : '#DBE4F0'}` }}>
      <div style={{ fontWeight: 800, color: '#1A56A0', marginBottom: 4 }}>🇨🇳 {L('중국 모드', '中国模式')}</div>
      <div>{L('기기 등록 코드', '本设备注册码')}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
        <code style={{ flex: 1, fontSize: 11, wordBreak: 'break-all', background: '#fff', padding: '6px 8px',
                       borderRadius: 6, border: '1px solid #e5e7eb', userSelect: 'all' }}>{token}</code>
        <button type="button" onClick={copy}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1A56A0', background: '#fff',
                   color: '#1A56A0', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {copied ? L('복사됨', '已复制') : L('복사', '复制')}
        </button>
      </div>
      <div style={{ marginTop: 6, color: '#6b7280' }}>
        {L('처음 쓰는 기기는 사장님이 이 코드를 등록해야 로그인돼요. 복사해서 사장님께 보내주세요.', '新设备需要老板先登记此码才能登录。请复制后发给老板。')}
      </div>
    </div>
  );
}

function LoginModals({ termsOpen, setTermsOpen, privacyOpen, setPrivacyOpen, lang = 'ko' }) {
  // V144: 약관 본문은 한국어 원문 — 중국어 화면에선 그렇다고 알려 준다
  const notice = lang === 'zh' ? '以下条款为韩文原文，以韩文为准。如有疑问，请咨询老板。' : null;
  return (
    <>
      <TermsOfServiceModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} notice={notice} okLabel={lang === 'zh' ? '确定' : undefined} />
      <PrivacyPolicyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} notice={notice} okLabel={lang === 'zh' ? '确定' : undefined} />
    </>
  );
}

export default Login;
